import { auditReports } from '@mi-front/api-clients';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface AodRunRestartButtonProps {
  run: auditReports.AodRun;
}

export function AodRunRestartButton({ run }: AodRunRestartButtonProps) {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const restartMutation = auditReports.useRestartAodRunRunActions();

  const handleRestart = async () => {
    try {
      await restartMutation.mutateAsync({ id: run.id });

      // Invalidate and refetch the run query
      queryClient.invalidateQueries({
        queryKey: auditReports.getOneBaseAodRunsControllerAodRunQueryKey(run.id),
      });

      setOpen(false);
    } catch (error) {
      console.error('Failed to restart AOD run:', error);
    }
  };

  // Only show restart button for runs that can be restarted
  if (!run.canRestart) {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<RestartAltIcon />}
        onClick={() => setOpen(true)}
      >
        {formatMessage({
          id: 'audit.aod.actions.restartRun',
          defaultMessage: 'Reiniciar ejecución',
        })}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          {formatMessage({
            id: 'audit.aod.actions.restartRun.title',
            defaultMessage: 'Reiniciar ejecución',
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {formatMessage(
              {
                id: 'audit.aod.actions.restartRun.message',
                defaultMessage:
                  '¿Estás seguro de que quieres reiniciar esta ejecución? Esta acción eliminará su reporte y volverá a encolar el procesamiento.',
              },
              { ruleName: run.rule.name },
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            disabled={restartMutation.isPending}
            sx={{
              marginRight: 1,
            }}
          >
            {formatMessage({
              id: 'common.actions.cancel',
              defaultMessage: 'Cancelar',
            })}
          </Button>
          <Button
            onClick={handleRestart}
            loading={restartMutation.isPending}
            color="warning"
            variant="contained"
            startIcon={<RestartAltIcon />}
          >
            {formatMessage({
              id: 'audit.aod.actions.restartRun',
              defaultMessage: 'Reiniciar ejecución',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
